import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Select, Tag, Button, Space, Spin, Alert, Drawer, Input, theme } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconHomeStar, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  getAdminHosts,
  getAdminHost,
  updateAdminHost,
  forceHostVerificationStatus,
  IAdminHostSummary,
} from '../../../services/AdminService';
import { IHostProfile } from '../../../interfaces/heavyDutyMachinery/IHostProfile';
import EmptyState from '../../../components/EmptyState';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, enumColumnFilters, booleanColumnFilters, booleanOnFilter } from '../../../helpers/adminTableHelpers';

const { Title, Text } = Typography;
const { TextArea } = Input;

const VERIFICATION_STATUS_COLORS: Record<string, string> = {
  NotStarted: 'default',
  Pending: 'processing',
  Verified: 'success',
  Rejected: 'error',
};

const VERIFICATION_STATUS_LABEL_KEYS: Record<string, string> = {
  NotStarted: TRANSLATIONS.SETTINGS_PAYOUTS_STATUS_NOT_STARTED,
  Pending: TRANSLATIONS.SETTINGS_PAYOUTS_STATUS_PENDING,
  Verified: TRANSLATIONS.SETTINGS_PAYOUTS_STATUS_VERIFIED,
  Rejected: TRANSLATIONS.SETTINGS_PAYOUTS_STATUS_REJECTED,
};

const PROFILE_STATUS_COLORS: Record<string, string> = { Pending: 'processing', Verified: 'success', Rejected: 'error' };
const PROFILE_STATUS_LABEL_KEYS: Record<string, string> = {
  Pending: TRANSLATIONS.ADMIN_HOSTS_PROFILE_STATUS_PENDING,
  Verified: TRANSLATIONS.ADMIN_HOSTS_PROFILE_STATUS_VERIFIED,
  Rejected: TRANSLATIONS.ADMIN_HOSTS_PROFILE_STATUS_REJECTED,
};

const VERIFICATION_STATUS_OPTIONS = ['NotStarted', 'Pending', 'Verified', 'Rejected'];
const PROFILE_STATUS_OPTIONS = ['Pending', 'Verified', 'Rejected'];
const ACCOUNT_TYPE_OPTIONS = ['Individual', 'Business'];

// HostProfile table filterable by AccountType/verification status, view/edit
// legal data, and force a change of identity-verification status.
const HostsSection: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const [hosts, setHosts] = useState<IAdminHostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [accountType, setAccountType] = useState<string | undefined>(undefined);
  const [verificationStatus, setVerificationStatus] = useState<string | undefined>(undefined);

  const [detail, setDetail] = useState<IHostProfile | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [legalForm, setLegalForm] = useState<Partial<IHostProfile>>({});
  const [savingLegal, setSavingLegal] = useState(false);

  const [newVerificationStatus, setNewVerificationStatus] = useState<string | undefined>(undefined);
  const [verificationReason, setVerificationReason] = useState('');
  const [savingVerification, setSavingVerification] = useState(false);

  const loadHosts = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminHosts(accountType, verificationStatus)
      .then(setHosts)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [accountType, verificationStatus]);

  useEffect(() => {
    loadHosts();
  }, [loadHosts]);

  const openDetail = (userId: string) => {
    setDetailLoading(true);
    setDetail({} as IHostProfile);
    setNewVerificationStatus(undefined);
    setVerificationReason('');
    getAdminHost(userId)
      .then((profile) => {
        setDetail(profile);
        setLegalForm(profile);
      })
      .catch(() => {
        message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
        setDetail(null);
      })
      .finally(() => setDetailLoading(false));
  };

  const handleSaveLegal = async () => {
    if (!detail) return;
    setSavingLegal(true);
    try {
      const updated = await updateAdminHost(detail.userId, {
        legalName: legalForm.legalName,
        taxId: legalForm.taxId,
        businessRegistrationNumber: legalForm.businessRegistrationNumber,
        contactPhone: legalForm.contactPhone,
        billingAddress: legalForm.billingAddress,
        insurancePolicyNumber: legalForm.insurancePolicyNumber,
      });
      setDetail(updated);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      loadHosts();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setSavingLegal(false);
    }
  };

  const handleForceVerification = async () => {
    if (!detail || !newVerificationStatus) return;
    if (!verificationReason.trim()) {
      message.error(translate(t, TRANSLATIONS.ADMIN_REASON_REQUIRED));
      return;
    }
    setSavingVerification(true);
    try {
      const updated = await forceHostVerificationStatus(detail.userId, newVerificationStatus, verificationReason.trim());
      setDetail(updated);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setVerificationReason('');
      loadHosts();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setSavingVerification(false);
    }
  };

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_HOSTS)}
      </Title>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_HOSTS_ACCOUNT_TYPE_FILTER_ALL)}
          style={{ maxWidth: 200, width: '100%' }}
          value={accountType}
          onChange={setAccountType}
          options={[
            { value: 'Individual', label: translate(t, TRANSLATIONS.ADMIN_HOSTS_ACCOUNT_TYPE_INDIVIDUAL) },
            { value: 'Business', label: translate(t, TRANSLATIONS.ADMIN_HOSTS_ACCOUNT_TYPE_BUSINESS) },
          ]}
        />
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_HOSTS_VERIFICATION_FILTER_ALL)}
          style={{ maxWidth: 220, width: '100%' }}
          value={verificationStatus}
          onChange={setVerificationStatus}
          options={VERIFICATION_STATUS_OPTIONS.map((value) => ({ value, label: translate(t, VERIFICATION_STATUS_LABEL_KEYS[value]) }))}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_HOSTS_LOAD_ERROR)} />
      ) : hosts.length === 0 ? (
        <EmptyState
          icon={<IconHomeStar size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_HOSTS_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_HOSTS_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminHostSummary>
          rowKey="userId"
          dataSource={hosts}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: record.userId,
            title: record.displayName,
            subtitle: record.legalName,
            badge: (
              <Tag color={VERIFICATION_STATUS_COLORS[record.idVerificationStatus]} style={{ marginInlineEnd: 0 }}>
                {translate(t, VERIFICATION_STATUS_LABEL_KEYS[record.idVerificationStatus])}
              </Tag>
            ),
            fields: [
              {
                label: translate(t, TRANSLATIONS.ADMIN_HOSTS_COL_ACCOUNT_TYPE),
                value: translate(
                  t,
                  record.accountType === 'Business'
                    ? TRANSLATIONS.ADMIN_HOSTS_ACCOUNT_TYPE_BUSINESS
                    : TRANSLATIONS.ADMIN_HOSTS_ACCOUNT_TYPE_INDIVIDUAL,
                ),
              },
              {
                label: translate(t, TRANSLATIONS.ADMIN_HOSTS_COL_PROFILE_STATUS),
                value: (
                  <Tag color={PROFILE_STATUS_COLORS[record.profileStatus]} style={{ marginInlineEnd: 0 }}>
                    {translate(t, PROFILE_STATUS_LABEL_KEYS[record.profileStatus])}
                  </Tag>
                ),
              },
              {
                label: translate(t, TRANSLATIONS.ADMIN_HOSTS_COL_BADGE),
                value: record.verifiedHostBadge ? <IconCheck size={16} color={token.colorSuccess} /> : '—',
              },
            ],
            actions: (
              <Button size="small" onClick={() => openDetail(record.userId)}>
                {translate(t, TRANSLATIONS.ADMIN_HOSTS_VIEW_BUTTON)}
              </Button>
            ),
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_HOSTS_COL_NAME),
              dataIndex: 'displayName',
              key: 'displayName',
              sorter: textSorter<IAdminHostSummary>('displayName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_HOSTS_COL_ACCOUNT_TYPE),
              dataIndex: 'accountType',
              key: 'accountType',
              filters: enumColumnFilters(ACCOUNT_TYPE_OPTIONS, (value) =>
                translate(t, value === 'Business' ? TRANSLATIONS.ADMIN_HOSTS_ACCOUNT_TYPE_BUSINESS : TRANSLATIONS.ADMIN_HOSTS_ACCOUNT_TYPE_INDIVIDUAL)
              ),
              onFilter: (value, record: IAdminHostSummary) => record.accountType === value,
              render: (value: string) =>
                translate(t, value === 'Business' ? TRANSLATIONS.ADMIN_HOSTS_ACCOUNT_TYPE_BUSINESS : TRANSLATIONS.ADMIN_HOSTS_ACCOUNT_TYPE_INDIVIDUAL),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_HOSTS_COL_LEGAL_NAME),
              dataIndex: 'legalName',
              key: 'legalName',
              sorter: textSorter<IAdminHostSummary>('legalName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_HOSTS_COL_VERIFICATION),
              dataIndex: 'idVerificationStatus',
              key: 'idVerificationStatus',
              filters: enumColumnFilters(VERIFICATION_STATUS_OPTIONS, (value) => translate(t, VERIFICATION_STATUS_LABEL_KEYS[value])),
              onFilter: (value, record: IAdminHostSummary) => record.idVerificationStatus === value,
              render: (value: string) => <Tag color={VERIFICATION_STATUS_COLORS[value]}>{translate(t, VERIFICATION_STATUS_LABEL_KEYS[value])}</Tag>,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_HOSTS_COL_PROFILE_STATUS),
              dataIndex: 'profileStatus',
              key: 'profileStatus',
              filters: enumColumnFilters(PROFILE_STATUS_OPTIONS, (value) => translate(t, PROFILE_STATUS_LABEL_KEYS[value])),
              onFilter: (value, record: IAdminHostSummary) => record.profileStatus === value,
              render: (value: string) => <Tag color={PROFILE_STATUS_COLORS[value]}>{translate(t, PROFILE_STATUS_LABEL_KEYS[value])}</Tag>,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_HOSTS_COL_BADGE),
              dataIndex: 'verifiedHostBadge',
              key: 'verifiedHostBadge',
              filters: booleanColumnFilters(translate(t, TRANSLATIONS.ADMIN_COMMON_YES), translate(t, TRANSLATIONS.ADMIN_COMMON_NO)),
              onFilter: booleanOnFilter<IAdminHostSummary>('verifiedHostBadge'),
              render: (value: boolean) => (value ? <IconCheck size={16} color={token.colorSuccess} /> : '—'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_HOSTS_COL_ACTIONS),
              key: 'actions',
              render: (_: unknown, record: IAdminHostSummary) => (
                <Button size="small" onClick={() => openDetail(record.userId)}>
                  {translate(t, TRANSLATIONS.ADMIN_HOSTS_VIEW_BUTTON)}
                </Button>
              ),
            },
          ]}
        />
      )}

      <Drawer title={translate(t, TRANSLATIONS.ADMIN_HOSTS_DETAIL_TITLE)} open={detail !== null} onClose={() => setDetail(null)} width={isMobile ? '100%' : 480}>
        {detailLoading || !detail?.userId ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          <>
            <Title level={5} style={{ marginBottom: 12 }}>
              {translate(t, TRANSLATIONS.ADMIN_HOSTS_EDIT_LEGAL_TITLE)}
            </Title>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              <div>
                <Text>{translate(t, TRANSLATIONS.ADMIN_HOSTS_LEGAL_NAME_LABEL)}</Text>
                <Input value={legalForm.legalName} onChange={(e) => setLegalForm((prev) => ({ ...prev, legalName: e.target.value }))} />
              </div>
              <div>
                <Text>{translate(t, TRANSLATIONS.ADMIN_HOSTS_TAX_ID_LABEL)}</Text>
                <Input value={legalForm.taxId} onChange={(e) => setLegalForm((prev) => ({ ...prev, taxId: e.target.value }))} />
              </div>
              {detail.accountType === 'Business' && (
                <div>
                  <Text>{translate(t, TRANSLATIONS.ADMIN_HOSTS_BUSINESS_REG_NUMBER_LABEL)}</Text>
                  <Input
                    value={legalForm.businessRegistrationNumber}
                    onChange={(e) => setLegalForm((prev) => ({ ...prev, businessRegistrationNumber: e.target.value }))}
                  />
                </div>
              )}
              <div>
                <Text>{translate(t, TRANSLATIONS.ADMIN_HOSTS_CONTACT_PHONE_LABEL)}</Text>
                <Input value={legalForm.contactPhone} onChange={(e) => setLegalForm((prev) => ({ ...prev, contactPhone: e.target.value }))} />
              </div>
              <div>
                <Text>{translate(t, TRANSLATIONS.ADMIN_HOSTS_BILLING_ADDRESS_LABEL)}</Text>
                <Input value={legalForm.billingAddress} onChange={(e) => setLegalForm((prev) => ({ ...prev, billingAddress: e.target.value }))} />
              </div>
              <div>
                <Text>{translate(t, TRANSLATIONS.ADMIN_HOSTS_INSURANCE_POLICY_LABEL)}</Text>
                <Input
                  value={legalForm.insurancePolicyNumber}
                  onChange={(e) => setLegalForm((prev) => ({ ...prev, insurancePolicyNumber: e.target.value }))}
                />
              </div>
              <Button type="primary" loading={savingLegal} onClick={handleSaveLegal}>
                {translate(t, TRANSLATIONS.ADMIN_HOSTS_SAVE_LEGAL_BUTTON)}
              </Button>
            </div>

            <Title level={5} style={{ marginBottom: 12 }}>
              {translate(t, TRANSLATIONS.ADMIN_HOSTS_FORCE_VERIFICATION_TITLE)}
            </Title>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <Text>{translate(t, TRANSLATIONS.ADMIN_HOSTS_VERIFICATION_STATUS_LABEL)}</Text>
                <Select
                  style={{ width: '100%' }}
                  value={newVerificationStatus}
                  onChange={setNewVerificationStatus}
                  options={VERIFICATION_STATUS_OPTIONS.map((value) => ({ value, label: translate(t, VERIFICATION_STATUS_LABEL_KEYS[value]) }))}
                />
              </div>
              <div>
                <Text>{translate(t, TRANSLATIONS.ADMIN_HOSTS_VERIFICATION_REASON_LABEL)}</Text>
                <TextArea rows={2} value={verificationReason} onChange={(e) => setVerificationReason(e.target.value)} />
              </div>
              <Button danger loading={savingVerification} disabled={!newVerificationStatus} onClick={handleForceVerification}>
                {translate(t, TRANSLATIONS.ADMIN_HOSTS_FORCE_VERIFICATION_BUTTON)}
              </Button>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default HostsSection;
